const unitToScale: { [key: string]: number } = {
  bcf: 1e-6,
  mmbbl: 1e-6,
  mmboe: 1e-6,
  mbbl: 1e-3,
  mboe: 1e-3,
  mmcf: 1e-3,
  scf: 1000
};

/** The unit a Y axis title is labelled in. */
export interface YAxisUnit {
  /**
   * The unit's numerator, written as the title writes it: "(MMcf/d)" gives
   * "MMcf", "(Mbbl per 100m)" gives "Mbbl". Empty when the title carries no
   * unit at all.
   */
  readonly numerator: string;
  /** The title wrote the unit as a per-day rate. */
  readonly perDay: boolean;
  /** Standard normalization's divisor as the title writes it: "per 100m", or empty. */
  readonly per: string;
  /** Multilinear normalization's trailing "(normalized)" annotation was present. */
  readonly normalized: boolean;
}

/**
 * Pull the unit out of a Y axis title.
 *
 * Parenthetical groups that are annotations, not units. Multilinear
 * normalization appends "(normalized)" AFTER the unit group
 * (e.g. "Cum Oil (Mbbl) (normalized)"), so it must be skipped when locating
 * the unit (EVA-6822). Standard normalization instead inlines the rate into
 * the unit group ("Cum Oil (Mbbl per 100m)") — the two never co-occur, but
 * the `per` stripping below tolerates it if they ever did.
 *
 * @param {string} title Y axis title of chart
 * @returns {YAxisUnit} the unit's numerator and whether it is a daily rate
 */
export const getYAxisUnit = (title: string): YAxisUnit => {
  const annotations = new Set(["normalized"]);

  const groups = [...title.matchAll(/\(([^)]+)\)/g)].map((match) => match[1]);
  let normalized = false;

  // The unit is the last parenthetical group. Walk right-to-left only to skip
  // trailing annotation groups, then resolve that single group as the unit —
  // we intentionally do NOT scan earlier groups for a unit match, so a
  // unit-like token inside a product group (e.g. "(O+C+W)") can't be picked up.
  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i];
    if (annotations.has(group.trim().toLowerCase())) {
      normalized = true;
      continue;
    }

    // Remove /d as the days unit stays the same after scaling, and strip the
    // 'per …' portion to isolate the numerator unit.
    //
    // Case-insensitively, because the title is the service's prose: "(bbl/D)"
    // and "(Mbbl Per 100m)" are the same units as their lower-case spellings,
    // and a strip that missed them left the whole group as the numerator - so
    // the scale silently fell back to 1 and the column kept a unit it was no
    // longer in. The numerator itself keeps the title's casing, because it is
    // what the Production Array prints in its header.
    return {
      numerator: group.replace(/\/d/gi, "").replace(/\s+per\s+.*$/gi, ""),
      perDay: /\/d/i.test(group),
      per: group.match(/\s+(per\s+.*)$/i)?.[1] ?? "",
      normalized
    };
  }

  return { numerator: "", perDay: false, per: "", normalized };
};

/**
 * Get the scaling for data values of Cum Time (default unit bbl, bcf) based off Y axis title
 *
 * @param {string} title Y axis title of chart
 * @returns {number} the number to scale data with
 */
export const scaleDataBasedOnYAxisUnits = (title: string): number => {
  const titleUnit = getYAxisUnit(title).numerator.toLowerCase();

  return titleUnit in unitToScale ? unitToScale[titleUnit] : 1;
};
